/* eslint-disable no-console */
import axios, { AxiosError, AxiosResponse } from "axios";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../utils/getApiUrl";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../utils/auth";
import { useCookies } from "react-cookie";
import { getAllResources } from "../utils/dataFetchers";
import { handleNavigatedFromExternalLink } from "./Sites/SitesPage/SitesPageUtils/handleNavigatedFromExternalLink";
import { getRefreshTimeout } from "../utils/getRefreshTimeout";
import { handleSetPermissions } from "../utils/handleSetPermissions";
import { useAppStore } from "../../appStore";
import { logger } from "../../utils/logger";
import { createApi } from "../utils/createApi";
import { isValidInternalPath, isValidAppRoute } from "../utils/urlValidation";
import { landingRouteFor } from "../utils/viewOnlyAccess";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";

export const Callback = ({
  setPermissions,
}: {
  setPermissions: any;
}) => {
  const queryString = window.location.search;
  const code = queryString.slice(6);
  const auth = useAuth();
  const navigate = useNavigate();
  const { flags } = useFeatureFlag();
  const wireguardPageEnabled = Boolean(flags?.showWireguardTunnelsPage);
  const baseURL = getApiUrl();
  const [statusMessage, setStatusMessage] = useState("Logging in...");
  const [cookies, setCookie] = useCookies(undefined);
  const { setUserPermissions } = useAppStore();

  const AcuateUI = axios.create({
    baseURL: `${baseURL}api/auth/`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${code}`,
    },
  });

  localStorage.setItem("getMFA", "false");

  const getPatrolOnlySites = () => {
    const AxiosUI = createApi(``);
    AxiosUI.get(`group/tree/?group_type=patrol`).then(
      (resopnse: AxiosResponse) => {
        localStorage.setItem(
          "groupTreesPatrolOnly",
          JSON.stringify(resopnse.data.results)
        );
        // navigate("/sites");
      },
      (error: AxiosError) => {
        logger.error("Error fetching patrol only sites:", error);
        localStorage.setItem("groupTreesPatrolOnly", JSON.stringify([]));
        // navigate("/sites");
      }
    );
  };

  // Default landing after login: patrol-only users go to their sites, and a
  // View Only user scoped to specific screens lands on the first one they can
  // see (ENG-1816). Everyone else keeps landing on the dashboard.
  const navigateToLanding = (reason: string) => {
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    const landing =
      landingRouteFor(userInfo?.permission, { wireguardPageEnabled }) ??
      "/dashboard";
    console.warn(`✅ CALLBACK: Navigating to ${landing} (${reason})`, {
      timestamp: new Date().toISOString(),
    });
    navigate(landing);
  };

  const navigateAfter = useCallback(
    async () => {
      await getAllResources(setPermissions, setUserPermissions, navigate)
        .then(async () => {
          // If the user has already navigated away from the callback page (e.g. via
          // URL or nav), do not redirect—leave them on their current page.
          if (!window.location.pathname.includes("callback")) {
            return;
          }
          // handleSetPermissions(navigate, setPermissions, setUserPermissions);
          // getPatrolOnlySites();
          // getNonPatrolSites();
          if (localStorage.getMFA === "true") {
            navigate(`/MFA`);
          }
          // Handle redirect URL after successful authentication
          else if (localStorage.redirectURL) {
            const redirectUrl = localStorage.redirectURL;

            // Validate the redirect URL for safety and route validity
            const isValidPath = isValidInternalPath(redirectUrl);
            const isValidRoute = isValidAppRoute(redirectUrl);

            if (isValidPath && isValidRoute) {
              // Check if the redirect URL contains "login" (should not redirect to login page)
              if (!redirectUrl.includes("login")) {
                // Special case: if redirect URL is just "/", use the landing route instead
                if (redirectUrl === "/") {
                  navigateToLanding("redirectURL was /");
                  localStorage.setItem("getMFA", "false");
                } else {
                  // Navigate to the validated path
                  navigate(redirectUrl);
                  localStorage.setItem("getMFA", "false");
                }
              } else {
                // If redirect URL contains "login", navigate based on user permissions
                navigateToLanding("redirectURL pointed at login");
                localStorage.setItem("getMFA", "false");
              }
            } else {
              // Invalid redirect URL, navigate to the landing route
              logger.warn(
                `Invalid redirect URL detected: ${redirectUrl}, redirecting to landing route`
              );
              navigateToLanding("invalid redirectURL");
              localStorage.setItem("getMFA", "false");
            }
            // Clean up the redirect URL
            localStorage.removeItem("redirectURL");
          } else {
            navigateToLanding("no redirectURL");
            localStorage.setItem("getMFA", "false");
          }
        })
        .catch((err: AxiosError) => {
          logger.error("Error loading resources:", err);

          // Only redirect if user is still on the callback page
          if (!window.location.pathname.includes("callback")) {
            return;
          }
          // Check if this is an MFA setup error
          if (localStorage.getItem("getMFA") === "true") {
            navigate("/MFA");
            return;
          }

          // Navigate to dashboard even if resources fail to load
          navigate("/dashboard");
        });
    },
    [
      // navigate, setPermissions, setUserPermissions
    ]
  );

  const getToken = useCallback(async () => {
    try {
      const response: AxiosResponse = await AcuateUI.get(
        `token/${queryString}`
      );

      // Use centralized auth state management
      const { authStateManager } = await import("../utils/authStateManager");
      authStateManager.setAuthState(
        response.data.token,
        response.data.token_secret
      );

      auth.login();
      navigateAfter(); // This handles navigation on success
    } catch (error) {
      const axiosError = error as AxiosError;
      logger.error("Error fetching token:", axiosError);

      // Check if this is an invalid_grant error (authorization code already used)
      const detailMessage = (axiosError.response?.data as any)?.detail;
      if (detailMessage && detailMessage.includes("invalid_grant")) {
        logger.warn(
          "Authorization code already consumed - this is expected if getToken was called twice"
        );
        return; // Don't show error or redirect, just return silently
      }

      setStatusMessage(
        `Login failed: ${
          detailMessage || axiosError.message
        }. Redirecting to login...`
      );

      setTimeout(() => {
        navigate("/login?error=failed", { replace: true });
      }, 3000);
    }
  }, [queryString, navigateAfter, getRefreshTimeout, navigate]);

  useEffect(() => {
    // Check if token already exists to prevent duplicate token requests
    const existingToken = cookies.token;
    if (existingToken) {
      logger.info("Token already exists, skipping getToken call");
      // If token exists, just navigate after without fetching a new token
      navigateAfter();
      return;
    }

    getToken();
  }, []);

  return (
    <div className="flex flex-col items-center justify-center align-center h-screen m-auto">
      <div className="lds-facebook font-black">
        <div></div>
        <div></div>
        <div></div>
      </div>
      <p className="mt-4 text-gray-600 text-[20px]">{statusMessage}</p>
    </div>
  );
};
